import React, { useState } from 'react';
import PlanSelectionModal from './PlanSelectionModal_WITH_PROMO';
import PostAdModal        from './PostAdModal';

/**
 * PostAdFlow — orchestrates the Post Ad flow:
 *   Step 1: PlanSelectionModal  → user sees plans with promo badges, picks a plan
 *   Step 2: PostAdModal         → user fills in ad details
 *     └─ If paid plan → PaymentModal (M-Pesa STK Push) appears on form submit
 *        └─ Payment confirmed → ad posted to backend
 *
 * Usage (controlled — you own the trigger button):
 *   const [showFlow, setShowFlow] = useState(false);
 *   <button onClick={() => setShowFlow(true)}>Post Ad</button>
 *   <PostAdFlow
 *     isOpen={showFlow}
 *     onClose={() => setShowFlow(false)}
 *     onAdPosted={handleAdPosted}
 *     noTrigger
 *   />
 *
 * Usage (self-contained trigger button):
 *   <PostAdFlow
 *     triggerLabel="+ Post Ad"
 *     triggerClassName={styles.postAdBtn}
 *     onAdPosted={handleAdPosted}
 *   />
 */
const PostAdFlow = ({
  // Controlled mode
  isOpen: controlledOpen,
  onClose: controlledClose,
  noTrigger = false,

  // Built-in trigger button
  triggerLabel     = '+ Post Ad',
  triggerClassName = '',

  // Callback
  onAdPosted,
}) => {
  const [internalOpen, setInternalOpen] = useState(false);

  // Steps: 'plan' | 'form'
  // Payment happens inside PostAdModal on form submit — not a separate step here
  const [step,         setStep]         = useState('plan');
  const [selectedPlan, setSelectedPlan] = useState(null);

  const isOpen  = controlledOpen !== undefined ? controlledOpen : internalOpen;
  const doClose = controlledClose || (() => setInternalOpen(false));

  const handleOpen = () => {
    setStep('plan');
    setSelectedPlan(null);
    if (controlledOpen === undefined) setInternalOpen(true);
  };

  const handleClose = () => {
    setStep('plan');
    setSelectedPlan(null);
    doClose();
  };

  // ── Step 1 → Step 2 ───────────────────────────────────────────────────────
  // All plans (free and paid) go straight to the form.
  // PostAdModal intercepts paid plan submits and shows PaymentModal internally.
  const handlePlanSelected = (plan) => {
    setSelectedPlan(plan);
    setStep('form');
  };

  const handleBackToPlan = () => {
    setStep('plan');
    setSelectedPlan(null);
  };

  const handleAdPosted = () => {
    handleClose();
    onAdPosted?.();
  };

  return (
    <>
      {/* Built-in trigger button */}
      {!noTrigger && (
        <button className={triggerClassName} onClick={handleOpen}>
          {triggerLabel}
        </button>
      )}

      {/* Step 1 — Plan selection with promo badges */}
      <PlanSelectionModal
        isOpen={isOpen && step === 'plan'}
        onClose={handleClose}
        onPlanSelected={handlePlanSelected}
      />

      {/* Step 2 — Ad form (PaymentModal fires internally on submit for paid plans) */}
      <PostAdModal
        isOpen={isOpen && step === 'form'}
        onClose={handleClose}
        onAdPosted={handleAdPosted}
        selectedPlan={selectedPlan}
        onBackToPlan={handleBackToPlan}
      />
    </>
  );
};

export default PostAdFlow;